import { z } from "zod";

/**
 * Org-scoped roles, one per persona on the marketing site.
 *
 * `platform_admin` is deliberately absent: OneCPA staff are not members of a
 * customer's firm. Platform access is a separate, time-boxed, fully audited
 * grant, so modelling it as just another org role would make it implicit.
 */
export const OrgRole = z.enum(["owner", "manager", "staff", "client_user"]);
export type OrgRole = z.infer<typeof OrgRole>;

export const ORG_ROLES = OrgRole.options;

/** Rank for "at least this role" comparisons. Higher is more privileged. */
const RANK: Record<OrgRole, number> = {
  client_user: 0,
  staff: 1,
  manager: 2,
  owner: 3,
};

export function atLeast(role: OrgRole, minimum: OrgRole): boolean {
  return RANK[role] >= RANK[minimum];
}

/** Office staff see the firm's data; a client user only ever sees their own. */
export function isOfficeRole(role: OrgRole): boolean {
  return role !== "client_user";
}

/**
 * Owner-exclusive surfaces, per the landing's persona pages. Enforced in the API
 * as well — this is a UI convenience, never the security boundary.
 */
export const OWNER_ONLY_CAPABILITIES = [
  "profitability.read",
  "office_overview.read",
  "workload.read",
  "engagement.read",
  "subscription.manage",
  "members.manage",
  "settings.manage",
] as const;

export type Capability = (typeof OWNER_ONLY_CAPABILITIES)[number];
